import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { orpc } from '@/lib/orpc-client'
import { useProjectEvent } from '@/lib/collab/presence-context'

/**
 * "HUB is writing the meeting notes…" (board m5gc #14). Shown at the top of
 * a document that is a HUB meeting's notes while the meeting has ended and
 * the MeetingTranscript workflow has not merged the notes yet; it clears
 * when the merge lands (the workflow flips `notes_status` and publishes
 * `meeting:changed`) and says so if the workflow gave up.
 */
export function MeetingNotesBanner({
  projectId,
  documentId,
}: {
  projectId: string
  documentId: string
}) {
  const { t } = useTranslation('documents')
  const [state, setState] = useState<'pending' | 'failed' | null>(null)

  const load = useCallback(async () => {
    try {
      const { meeting } = await orpc.meetings.forDocument({ projectId, documentId })
      setState(
        meeting?.kind === 'hub' && meeting.roomStatus === 'ended' && meeting.notesStatus !== 'done'
          ? meeting.notesStatus === 'failed'
            ? 'failed'
            : meeting.notesStatus === 'pending'
              ? 'pending'
              : null
          : null
      )
    } catch {
      setState(null)
    }
  }, [projectId, documentId])

  useEffect(() => {
    void load()
  }, [load])
  useProjectEvent(['meeting:changed', 'meeting:live'], () => void load())

  if (!state) return null
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="meeting-notes-banner"
      data-state={state}
      className={
        state === 'pending'
          ? 'flex shrink-0 items-center justify-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100'
          : 'flex shrink-0 items-center justify-center gap-2 border-b border-red-300 bg-red-50 px-4 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100'
      }
    >
      {state === 'pending' ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      )}
      <span>{state === 'pending' ? t('meetingNotes.writing') : t('meetingNotes.failed')}</span>
    </div>
  )
}
